import { useLocalStorage } from '@mantine/hooks';
import React, { useState } from 'react';

import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/number-formatter';
import { useAuthContext } from 'views/auth/Auth';

import { useCommandCenterHouseholds } from '../household/household-queries';
import { useAggregatedInflows } from '../portfolio/reporting-queries';
import {
    DEFAULT_PRACTICE_PULSE_PERIOD,
    getPracticePulseDateRange,
    normalizePracticePulsePeriod,
    PRACTICE_PULSE_PERIOD_STORAGE_KEY,
} from './practice-pulse-periods';

export const KeyMetrics = () => {
    const [storedPeriod] = useLocalStorage<string>({
        key: PRACTICE_PULSE_PERIOD_STORAGE_KEY,
        defaultValue: DEFAULT_PRACTICE_PULSE_PERIOD,
    });
    const selectedPeriod = normalizePracticePulsePeriod(storedPeriod);
    const { from: startDate, to: endDate } = getPracticePulseDateRange(selectedPeriod);

    const { data: households, isLoading: isHouseholdsLoading } = useCommandCenterHouseholds();
    const { data: inflows, isLoading: inflowsLoading } = useAggregatedInflows(startDate, endDate);
    const { user } = useAuthContext();

    const totalAum = households?.reduce((acc, h) => acc + (h.aum ? Number(h.aum) : 0), 0);
    const householdCount = households?.reduce((acc, h) => acc + (h.aum ? 1 : 0), 0);
    const isMetricsLoading = isHouseholdsLoading || inflowsLoading;

    const greetingName = user?.firstName?.trim();
    const [timeOfDayGreeting] = useState(() => {
        const hour = new Date().getHours();
        return hour < 12 ? 'Good Morning' : hour < 18 ? 'Good Afternoon' : 'Good Evening';
    });
    const greeting = greetingName ? `${timeOfDayGreeting}, ${greetingName}` : timeOfDayGreeting;

    const totalAumLabel = formatCurrency(totalAum ?? 0, 'USD');
    const inflowsLabel = formatCurrency(inflows, 'USD');
    const householdCountLabel = new Intl.NumberFormat('en-US').format(householdCount ?? 0);

    return (
        <div className="col-span-12 flex flex-col gap-4 pt-6 pb-2" data-node-id="7:475">
            <h2
                className="text-foreground text-[40px] leading-[0.9]"
                style={{ fontFamily: 'ivypresto-headline, Georgia, serif' }}
                data-node-id="7:463"
            >
                {greeting}
            </h2>
            <p
                className="font-halyard text-[16px] leading-[1.2] tracking-[-0.02em] text-[#5A4F4E]"
                data-node-id="7:482"
            >
                Total AUM is at{' '}
                <span
                    className={cn(
                        'inline-block rounded-[2px] bg-[#D7ECEF]/60 px-1 font-semibold text-[#3C8A95]',
                        isMetricsLoading && 'ndvr-skeleton text-transparent',
                    )}
                >
                    {totalAumLabel}
                </span>{' '}
                across {householdCountLabel} Households with{' '}
                <span
                    className={cn(
                        'inline-block rounded-[2px] bg-[#AEE1B4]/50 px-1 font-semibold text-[#077440]',
                        isMetricsLoading && 'ndvr-skeleton text-transparent',
                    )}
                >
                    {inflowsLabel}
                </span>{' '}
                in inflows {selectedPeriod === 'YTD' ? 'YTD' : selectedPeriod.toLowerCase()}.
            </p>
        </div>
    );
};
